/**
 * Lava-lamp background — five colored blobs that drift slowly with vertical
 * bias and morph their border-radius for an organic shape.
 *
 * Two rendering paths:
 *   - Chromium/Firefox: SVG goo filter on the parent merges overlapping
 *     blobs into a single liquid silhouette (classic metaball technique).
 *   - Safari: the SVG `feColorMatrix` alpha-amplification step clips the
 *     gradient and renders blobs as hard disks. We detect Safari at module
 *     load and skip applying the goo filter, leaving each blob as a soft
 *     CSS-blurred glow orb.
 */
import { useEffect } from "react";

const isSafari =
  typeof navigator !== "undefined" &&
  /^((?!chrome|android|crios|fxios).)*safari/i.test(navigator.userAgent);

// Jitter the initial position of each corner-anchored blob so we don't get
// the same "four spheres in the exact corners" composition every load.
// Per-axis offsets are randomized once on module load (so they stay stable
// for the lifetime of the page) and applied as `margin-*` overrides — the
// CSS keyframes drive `transform: translate(...)`, so this jitter coexists
// with the bob/morph animations without fighting them.
function rand(min: number, max: number) {
  return Math.round(min + Math.random() * (max - min));
}
const jitter = {
  // Each blob is anchored to two CSS sides (e.g. blob a is top+left). We
  // randomize the margin on *those same two sides*, in the direction that
  // shifts the blob into the viewport. The range keeps blobs partly off-
  // screen so the soft edge is still hidden but the composition shifts.
  a: { marginTop: rand(-40, 80), marginLeft: rand(-40, 80) },
  b: { marginBottom: rand(-40, 80), marginRight: rand(-40, 80) },
  c: { marginTop: rand(-40, 80), marginRight: rand(-40, 80) },
  d: { marginBottom: rand(-40, 80), marginLeft: rand(-40, 80) },
};

export default function Lava() {
  // Mouse parallax — feed cursor position into CSS variables the blobs react
  // to. rAF-throttled. Lives here (not in the screen) so every screen that
  // renders <Lava /> gets the effect.
  useEffect(() => {
    let raf = 0;
    let nextX = 0;
    let nextY = 0;
    const onMove = (e: MouseEvent) => {
      nextX = (e.clientX / window.innerWidth) * 2 - 1;
      nextY = (e.clientY / window.innerHeight) * 2 - 1;
      if (!raf) {
        raf = requestAnimationFrame(() => {
          document.documentElement.style.setProperty("--mx", `${nextX}`);
          document.documentElement.style.setProperty("--my", `${nextY}`);
          raf = 0;
        });
      }
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <>
      {!isSafari && (
        <svg
          aria-hidden
          style={{ position: "absolute", width: 0, height: 0 }}
          focusable="false"
        >
          <defs>
            <filter id="lava-goo">
              <feGaussianBlur in="SourceGraphic" stdDeviation="30" />
              <feColorMatrix
                values="
                  1 0 0 0 0
                  0 1 0 0 0
                  0 0 1 0 0
                  0 0 0 22 -10"
              />
            </filter>
          </defs>
        </svg>
      )}
      <div className={`lava ${isSafari ? "lava--no-goo" : ""}`}>
        <div className="lava__blob lava__blob--a" style={jitter.a} />
        <div className="lava__blob lava__blob--b" style={jitter.b} />
        <div className="lava__blob lava__blob--c" style={jitter.c} />
        <div className="lava__blob lava__blob--d" style={jitter.d} />
        <div className="lava__blob lava__blob--e" />
      </div>
    </>
  );
}
